<template>
	<div
		v-if="
			isError &&
			type !== GameErrorType.BE_TIMEOUT_ERROR &&
			type !== GameErrorType.NETWORK_DISCONNECT_DURING_REQUEST &&
			type !== GameErrorType.PARALLEL_SESSION_ERROR
		"
		class="error-overlay"
	>
		<div
			class="error-content"
			:class="[
				`error-content--${type}`,
				{
					'is-be-error': isBeError,
				},
			]"
		>
			<h1 v-if="isBeError">BE Error</h1>
			<h1 v-else>FE Error</h1>
			<p v-html="messageHtml" />
			<p>
				<button
					v-if="isBeError || isParallelSessionError"
					type="button"
					@click="reload"
				>
					Reload
				</button>
				<button
					v-if="!isParallelSessionError"
					type="button"
					@click="close"
				>
					Continue
				</button>
			</p>
		</div>
	</div>
	<slot v-if="slots.default" />
</template>

<script lang="ts" setup>
import useGameTicker from "@/hooks/useGameTicker.ts";
import useSocketService from "@/hooks/useSocketService";
import Communicator from "@/libs/AppCommunicatior";
import GameError, { GameErrorType } from "@/libs/GameError";
import {
	PopupDictionary,
	PopupType,
	usePopupStore,
} from "@/store/usePopupStore";
import useRuntimeStore from "@/store/useRuntimeStore";
import { SkinTypes, StatusTypes } from "@/types/ui";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { computed, markRaw, onErrorCaptured, ref, useSlots, watch } from "vue";

const CONSOLE_COLOR = [
	"%c ERROR ",
	"background-color:maroon;color:white;font-weight:700;",
];

const NL_REGEXP = /\n/g;

const slots: ReturnType<typeof useSlots> = useSlots();

const socketService = markRaw(useSocketService());
const communicator = markRaw(Communicator.getInstance());

const runtimeStore = useRuntimeStore();
const { isLoadingDone } = storeToRefs(runtimeStore);

const type = ref<GameErrorType | null>(null);
const message = ref<string | null>(null);
const messageHtml = computed((): string | null =>
	message.value ? message.value.replace(NL_REGEXP, "<br />") : null,
);

const isError = computed((): boolean => message.value !== null);

const isBeError = computed(
	(): boolean =>
		!!type.value &&
		[
			GameErrorType.BE_ERROR,
			GameErrorType.BE_TIMEOUT_ERROR,
			GameErrorType.BE_VALIDATION_ERROR,
			GameErrorType.NETWORK_DISCONNECT_DURING_REQUEST,
		].includes(type.value),
);

const isParallelSessionError = computed(
	(): boolean =>
		!!type.value &&
		[GameErrorType.PARALLEL_SESSION_ERROR].includes(type.value),
);

onErrorCaptured((error: Error): false | void => {
	if (isParallelSessionError.value) return false;

	if (error instanceof GameError) {
		type.value = error.type;
		message.value = error.message;

		console.error(...CONSOLE_COLOR, "Captured game error:", error);
		return false;
	}

	// if (error instanceof Error) {
	// 	type.value = GameErrorType.FE_ERROR;
	// 	message.value = error.message;
	// 	return false;
	// }
});

window.addEventListener(
	"error",
	(event: ErrorEvent): void => {
		if (isParallelSessionError.value) return;

		if (event.error instanceof GameError) {
			type.value = event.error.type;
			message.value = event.error.message;
		}

		// if (event.error instanceof Error) {
		// 	type.value = GameErrorType.FE_ERROR;
		// 	message.value = event.error.message;
		// }
	},
	{ passive: true },
);

window.addEventListener(
	"unhandledrejection",
	(event: PromiseRejectionEvent): void => {
		if (isParallelSessionError.value) return;

		if (event.reason instanceof GameError) {
			type.value = event.reason.type;
			message.value = event.reason.message;
		}

		// if (event.reason instanceof Error) {
		// 	type.value = GameErrorType.FE_ERROR;
		// 	message.value = event.reason.message;
		// }
	},
	{ passive: true },
);

watch(
	(): boolean => isError.value,
	(value: boolean): void => {
		if (!value) return;
		if (isLoadingDone.value) return;
		communicator.showErrorDuringLoading(message.value ?? "Unknown error");
	},
);

watch(
	(): boolean => isParallelSessionError.value,
	(value: boolean): void => {
		if (!value) return;
		useGameTicker().stop();
		showReloadPopup();
	},
);

const showReloadPopup = (): void => {
	usePopupStore().showPopup({
		id: PopupDictionary.PURCHASE_SUCCESS,
		type: PopupType.INFO,
		heading: translate("ui.logged_out"),
		body: translate("ui.logged_out_info"),
		skin: SkinTypes.LIGHT,
		isCentered: true,
		closeable: false,
		height: 13,
		force: true,
		isPriority: true,
		actions: [
			{
				label: translate("button.sign_in"),
				buttonType: StatusTypes.PRIMARY,
				callback: async (): Promise<void> => {
					socketService.clear();
					socketService.reload();
				},
			},
		],
	});
};

const close = (): void => {
	type.value = null;
	message.value = null;
};

const reload = (): void => {
	socketService.clear();
	socketService.reload();
};
</script>

<style lang="scss" scoped>
.error-overlay {
	@include flex-col-center;
	position: fixed;
	top: 0;
	left: 0;
	z-index: 1000;
	backdrop-filter: blur(0.5rem);
	background-color: rgba(255, 255, 255, 0.5);
	width: 100%;
	height: 100%;

	.error-content {
		@include flex-col-center;
		@include space-v(1rem);
		box-shadow: 0 0 0.5rem rgba(0, 0, 0, 0.5);
		border-radius: 1rem;
		background: linear-gradient(to right bottom, goldenrod, crimson);
		padding: 1.5rem;
		min-width: 24rem;
		color: white;
		font-weight: 500;
		text-align: center;

		h1 {
			font-weight: 700;
			font-size: 1.25rem;
		}

		p {
			font-size: 1rem;
		}

		button {
			transition: background-color 0.125s;
			cursor: pointer;
			border: 0.125rem solid white;
			border-radius: 0.5rem;
			background-color: transparent;
			padding: 0.5rem 1rem;
			color: white;
			font-size: 1rem;

			&:hover {
				background-color: rgba(255, 255, 255, 0.1);
			}

			&:not(:last-child) {
				margin-right: 0.5rem;
			}
		}

		&.is-be-error {
			background: linear-gradient(
				to right bottom,
				palevioletred,
				steelblue
			);
		}
	}
}
</style>
